import { getFileDownloadUrl } from '@/shared/api/assets.api';
import { isAssetId } from '@/shared/lib/asset-id';

/** The largest image a widget gets inline; past this an asset is a download, not a picture. */
export const MAX_ASSET_IMAGE_BYTES = 5 * 1024 * 1024;

const loaded = new Map<string, Promise<string>>();

function dataUrlOf(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('The image could not be read'));
    reader.readAsDataURL(blob);
  });
}

async function load(assetId: string): Promise<string> {
  const response = await fetch(await getFileDownloadUrl(assetId));
  if (!response.ok) throw new Error(`The image could not be loaded (${response.status})`);
  const blob = await response.blob();
  if (!blob.type.startsWith('image/')) throw new Error('The asset is not an image');
  if (blob.size > MAX_ASSET_IMAGE_BYTES) throw new Error('The image is too large to show');
  return dataUrlOf(blob);
}

/**
 * An asset as an inline image a widget can show. The widget's frame is sandboxed without the
 * person's sign-in, so the page loads the asset as them, through its signed URL, and hands the
 * frame the bytes. Each asset is loaded once; a failure is forgotten so it can be tried again.
 */
export function assetImageSrc(assetId: string): Promise<string> {
  if (!isAssetId(assetId)) return Promise.reject(new Error('Not an asset id'));
  const known = loaded.get(assetId);
  if (known) return known;
  const loading = load(assetId).catch((error: unknown) => {
    loaded.delete(assetId);
    throw error;
  });
  loaded.set(assetId, loading);
  return loading;
}
